import {
  AskingPriceLike,
  resolveAskingPrice,
  resolveAskingPriceAmount,
} from "@/common/utils/asking-price.utils";

/**
 * Comparable-alternatives engine for a sold listing (WHO-4840, GEO-1).
 *
 * A sold PDP is only worth keeping alive if it can point the visitor at
 * something they can actually buy. The first cut of that (PR #2217)
 * matched on brand plus the deepest category and stopped there, which is
 * both too loose (any de Sede stool counts as an alternative to any other
 * de Sede stool) and too brittle (a brandless listing got nothing).
 *
 * This module replaces it with an explicit, ranked ladder. It is
 * deliberately pure: it takes a `search` callback rather than an Apollo
 * client, imports no generated GraphQL types, and does no rendering. That
 * keeps the rules testable on their own, which matters because the rules
 * are the part a human will want to argue with.
 *
 * ## The rules (Thomas, 2026-08-01)
 *
 * "The exact sub category is absolutely leading first." The item's most
 * specific category node leads every rung, and brand is pinned beside it.
 * Neither is ever dropped. The leaf is the whole point: not "interior" or
 * "art" but "interior > seating > sofas > 2 seaters". Without it a sold
 * sofa can return a lamp, because "de Sede + leather + modern" is true of
 * furniture right across the catalogue.
 *
 * Degradation ladder, first level that yields at least one alternative
 * wins:
 *
 *   L0  subcategory + brand + material + style + condition + colour
 *   L1  drop colour
 *   L2  drop colour + material
 *   L3  drop colour + material + condition
 *   L4  drop colour + material + condition + style
 *   L5  nothing matched: no module, link to the subcategory page with
 *       the brand filter pre-applied
 *
 * Subcategory and brand are never dropped. Everything else is, style
 * included.
 *
 * ## Why style is droppable (Thomas, 2026-08-01)
 *
 * "Drop style, it may not always be defined or defined correctly."
 *
 * Attribute coverage across 800 live PUBLISHED products: category 100%,
 * condition 100%, quality 100%, colour 99%, material 97%, brand 50%,
 * style 33%, brand and style both 23%. Style is the thinnest field in the
 * catalogue by a wide margin.
 *
 * Pinning it was actively harmful, not merely strict. An item that HAS a
 * style can never match one that lacks it, so
 * `ahrend-350-vergaderstoel-donkerblauw` (Style: modern) dead-ended while
 * two other Ahrend conference chairs sat in the same subcategory with no
 * Style attribute at all. L4 exists for exactly that: it keeps style
 * leading wherever style discriminates, and gets out of the way when
 * style is simply missing from the data.
 *
 * ## Leaf vs parent category, measured on live data (2026-08-01)
 *
 * `CATEGORY` takes a category SLUG, and a parent slug rolls up all of its
 * descendants. Against `BRAND=de-sede` + `AVAILABILITY=true`:
 *
 *   fauteuils           (leaf, level 2)    142
 *   stoelen-fauteuils   (parent, level 1)  197
 *   meubels             (root, level 0)    414
 *   no category filter                     414
 *
 * The root is indistinguishable from no filter at all for this brand, so
 * taking the leaf is not a refinement, it is the whole constraint. Passing
 * the `route` instead of the slug ("meubels/stoelen-fauteuils/fauteuils")
 * returns 0, silently: it is not a validation error, so a wrong value here
 * would look like "no alternatives exist" rather than like a bug.
 *
 * Price is not a limiting factor by default. A level that returns more
 * than `PRICE_BAND_THRESHOLD` matches is narrowed to +/-50% of the sold
 * item's asking price. If that band leaves fewer than
 * `MIN_BANDED_RESULTS`, the band is thrown away and the unbanded set is
 * ranked by price proximity instead. The price rule can therefore never
 * make a level fail.
 */

/**
 * Filter entry as `searchProducts(input: {filters: [...]})` expects it.
 *
 * Verified against the live gateway on 2026-08-01, because the shape is
 * not guessable and the wrong shape fails silently rather than erroring:
 *
 *  - Two entries with the SAME key are ANDed. `COLOR=black` plus
 *    `COLOR=white` returned 5,463 items: things that are both.
 *  - One entry with comma-separated values is ORed. `COLOR=black,white`
 *    returned 44,825, which is exactly 27,042 + 23,246 - 5,463.
 *  - `PRICE` is the odd one out: its comma is a RANGE separator,
 *    `"min,max"`.
 *
 * A criterion the sold item holds several values for (two materials, three
 * colours) is therefore emitted as a single comma-joined OR entry. ANDing
 * them would demand an alternative carry every one of the original's
 * materials, which collapses to nothing on real inventory.
 */
export type SoldAlternativeFilter = {
  key: string;
  value: string;
};

/** Ladder rungs, in the order they are tried. */
export const ALTERNATIVES_LEVEL = {
  /** subcategory + brand + material + style + condition + colour */
  L0: "L0",
  /** L0 without colour */
  L1: "L1",
  /** L0 without colour and material */
  L2: "L2",
  /** L0 without colour, material and condition */
  L3: "L3",
  /** Subcategory and brand only. The last rung that still searches. */
  L4: "L4",
  /** Nothing matched. No module; link to the subcategory page. */
  L5: "L5",
} as const;

export type AlternativesLevel =
  (typeof ALTERNATIVES_LEVEL)[keyof typeof ALTERNATIVES_LEVEL];

/**
 * How many alternatives the page shows by default.
 *
 * Four rather than two is still under review: the preview deployment
 * carries a 2-vs-4 toggle so the choice can be made by looking at real
 * pages instead of arguing about it. `SoldAlternatives` takes the count as
 * a prop, so changing this constant is the only edit needed to settle it.
 */
export const ALTERNATIVES_DISPLAY_COUNT = 4;

/**
 * Above this many matches, a level is narrowed by the price band. At or
 * below it the level is already specific enough that filtering further
 * would only throw away good answers.
 */
export const PRICE_BAND_THRESHOLD = 5;

/** A band that leaves fewer than this is discarded, not honoured. */
export const MIN_BANDED_RESULTS = 2;

/** +/-50% around the sold item's asking price. */
export const PRICE_BAND_RATIO = 0.5;

/**
 * How many items to pull per level. Larger than
 * `ALTERNATIVES_DISPLAY_COUNT` so the price-proximity fallback has a real
 * set to rank, and so self-exclusion cannot empty the page.
 */
export const ALTERNATIVES_FETCH_LIMIT = 24;

/**
 * The wire key for the "condition" criterion is `QUALITY`, not
 * `CONDITION`. Both exist in the `SearchFilterKey` enum, but they are
 * different things and only one of them is what Whoppah means by
 * condition:
 *
 *  - `QUALITY` is `GOOD | GREAT | PERFECT`. It is the field the PDP
 *    renders under the label `t("pdp:conditions")`
 *    (`info/ProductDetailsInfoAttributes.tsx`) and the field behind the
 *    PLP's "Condition" filter panel (`SearchFiltersPanelEntryQuality`,
 *    which maps to `SearchFilterKey.Quality`).
 *  - `CONDITION` is `NEW | USED`, is never rendered anywhere in the UI,
 *    and does not discriminate: on a de Sede armchair search it kept
 *    139 of 142 items, so matching on it is close to matching on nothing.
 *
 * Thomas's criteria list is the PLP filter panel read top to bottom, so
 * "condition" is `QUALITY`.
 */
export const CONDITION_FILTER_KEY = "QUALITY";

export const AVAILABILITY_FILTER: SoldAlternativeFilter = {
  key: "AVAILABILITY",
  value: "true",
};

/**
 * A `Price` as the schema defines it, deep-partial. `amount: Float!` and
 * `currency: Currency!` are non-null in the gateway, so a fixture with
 * `amount: null` is a shape the gateway cannot produce and the compiler
 * rejects it.
 */
type PriceLike = AskingPriceLike;

/*
  `| undefined` on every one of these, and on the array ELEMENTS, is
  load-bearing rather than defensive noise.

  Two different generated types flow through this module. `productBySlug`
  gives nullable fields, so `| null` covers it. Search results arrive as
  `ProductUnknown`, which is `PartialObjectDeep<Product, {
  recurseIntoArrays: true }>`: that makes every field optional AND every
  array element `T | undefined`. Without the element-level `| undefined`,
  passing a search result here fails with "Type 'undefined' is not
  assignable to type 'CategoryLike'", which is exactly how the first
  attempt at the price anchor broke the build.

  It is invisible locally, because the throwaway `src/Apollo/schema.ts`
  stub used to run these tests without a gateway token declares `Product`
  as `any`. Only the real codegen in CI can see it.
*/
type AttributeLike =
  | {
      __typename?: string | null;
      slug?: string | null;
      title?: string | null;
    }
  | null
  | undefined;

type CategoryLike =
  | {
      slug?: string | null;
      route?: string | null;
      level?: number | null;
      title?: string | null;
    }
  | null
  | undefined;

/**
 * Structural shape of the fields the engine reads.
 *
 * Deliberately not the generated `ProductBySlugNonNullable`: those types
 * come from `src/Apollo/schema.ts`, which is gitignored codegen, so a
 * module importing them cannot be unit-tested without a gateway token.
 * `sold-product.utils.ts` already sets this precedent with
 * `SoldProductLike`.
 */
export type AlternativeProductLike = {
  id?: string | null;
  quality?: string | null;
  brand?: { slug?: string | null } | null;
  categories?: Array<CategoryLike> | null;
  attributes?: Array<AttributeLike> | null;
  buy_now_price?: PriceLike;
  auction?: {
    buy_now_price?: PriceLike;
    minimum_bid?: PriceLike;
    sold_at?: PriceLike;
  } | null;
} | null;

/** One page of search results, as the ladder needs to see them. */
export type AlternativesSearchResult<TItem> = {
  items: TItem[];
  /** Total matches for the filter set, not the length of `items`. */
  count: number;
};

export type AlternativesSearch<TItem> = (
  filters: SoldAlternativeFilter[],
  limit: number
) => Promise<AlternativesSearchResult<TItem>>;

/** What the price rule ended up doing at the level that fired. */
export const PRICE_BAND_OUTCOME = {
  /** 5 or fewer matches, so the band was never needed. */
  NotNeeded: "not-needed",
  /** More than 5 matches, band applied, band kept. */
  Applied: "applied",
  /** Band applied but left fewer than 2, so it was discarded and the
   *  unbanded set was ranked by price proximity instead. */
  DiscardedTooNarrow: "discarded-too-narrow",
  /** More than 5 matches but the item has no asking price to band around. */
  NoAskingPrice: "no-asking-price",
  /** The ladder never got as far as needing a decision. */
  NotApplicable: "not-applicable",
} as const;

export type PriceBandOutcome =
  (typeof PRICE_BAND_OUTCOME)[keyof typeof PRICE_BAND_OUTCOME];

export type SoldAlternativesResult<TItem> = {
  /** The rung that produced `alternatives`, or `L5` when none did. */
  level: AlternativesLevel;
  alternatives: TItem[];
  /** Filters used at the level that fired. Empty at L5. */
  filters: SoldAlternativeFilter[];
  priceBand: PriceBandOutcome;
  /** Matches at the level that fired, excluding the sold item itself. */
  matchCount: number;
  /** Only set at L5: where to send the visitor instead. */
  categoryFallback: SoldCategoryFallback | null;
};

/**
 * Where an L5 sends the visitor: the item's own subcategory page, with the
 * brand carried over so they land on "de Sede armchairs" and not on every
 * armchair Whoppah has ever listed.
 */
export type SoldCategoryFallback = {
  slug: string;
  route: string | null;
  brand: string | null;
};

/** The criteria the ladder can filter on, read off the sold item. */
export type SoldAlternativeCriteria = {
  brand: string | null;
  category: string | null;
  categoryRoute: string | null;
  material: string[];
  style: string[];
  condition: string | null;
  colour: string[];
  askingPrice: number | null;
};

export type CriterionKey =
  | "brand"
  | "category"
  | "material"
  | "style"
  | "condition"
  | "colour";

/**
 * Which similarity criterion each wire filter key represents.
 *
 * Only the six match criteria appear here. `AVAILABILITY` is a
 * precondition rather than a similarity, and `PRICE` is a band applied on
 * top of a level rather than something the items were matched on, so
 * neither is a criterion and neither may be described as one.
 */
export const MATCH_CRITERION_BY_FILTER_KEY: Record<string, CriterionKey> = {
  CATEGORY: "category",
  BRAND: "brand",
  MATERIAL: "material",
  STYLE: "style",
  [CONDITION_FILTER_KEY]: "condition",
  COLOR: "colour",
};

/**
 * The criteria a filter set actually matched on, in emission order.
 *
 * The page has to say what it did, not what the rung is nominally called.
 * A criterion the sold item does not carry is omitted from the filters by
 * design, so a static sentence naming the rung's full criteria set can
 * claim a match that never happened: `art-nouveau-tafellamp-8` has no
 * brand, matched at L0 without a BRAND filter, and returned four
 * different brands under the words "the same brand".
 */
export const describeMatchedCriteria = (
  filters: SoldAlternativeFilter[]
): CriterionKey[] =>
  filters
    .map(filter => MATCH_CRITERION_BY_FILTER_KEY[filter.key])
    .filter((criterion): criterion is CriterionKey => !!criterion);

/**
 * Which criteria each rung filters on.
 *
 * Read this table as the specification: it is the only place the ladder
 * is defined, and the tests assert against it directly.
 */
export const LEVEL_CRITERIA: Record<
  Exclude<AlternativesLevel, "L5">,
  CriterionKey[]
> = {
  L0: ["category", "brand", "material", "style", "condition", "colour"],
  L1: ["category", "brand", "material", "style", "condition"],
  L2: ["category", "brand", "style", "condition"],
  L3: ["category", "brand", "style"],
  L4: ["category", "brand"],
};

const LEVEL_ORDER: Array<Exclude<AlternativesLevel, "L5">> = [
  "L0",
  "L1",
  "L2",
  "L3",
  "L4",
];

const attributeSlugs = (
  attributes: Array<AttributeLike> | null | undefined,
  typename: string
) =>
  (attributes ?? [])
    .filter(attribute => attribute?.__typename === typename)
    .map(attribute => attribute?.slug)
    .filter(
      (slug): slug is string => typeof slug === "string" && !!slug.trim()
    );

/**
 * Deepest category, which is the one that says what the thing IS.
 * `meubels` is a section; `fauteuils` is a product.
 */
const deepestCategory = (categories: Array<CategoryLike> | null | undefined) =>
  (categories ?? [])
    .filter(category => !!category?.slug)
    .reduce<CategoryLike>(
      (deepest, category) =>
        !deepest || (category?.level ?? 0) > (deepest.level ?? 0)
          ? category
          : deepest,
      null
    );

/**
 * Below this relative difference the anchor is not worth a line of copy.
 *
 * "EUR 4 less than the piece you viewed" is noise on a EUR 400 chair: it
 * reads as a rounding error and it spends a line that the cards need. Five
 * percent is the point at which the number starts to be a reason.
 */
export const PRICE_ANCHOR_MIN_RATIO = 0.05;

export type PriceAnchor = {
  /** Cheaper or dearer than the piece the visitor came for. */
  direction: "less" | "more";
  /** Absolute difference, always positive. */
  amount: number;
  currency: string | null;
};

/**
 * How an alternative's asking price compares with the sold item's.
 *
 * They arrived wanting a EUR 400 chair. A card at EUR 250 should say so:
 * that comparison is the strongest single click driver on any comparison
 * surface, and we hold both numbers already.
 *
 * Returns null, and the card says nothing, when:
 *
 *  - either asking price is missing. Half the catalogue has gaps; an anchor
 *    computed against a missing number would be invented.
 *  - the two are priced in different KNOWN currencies. The subtraction
 *    would be arithmetic on incomparable units.
 *
 *    A currency absent on one side is not a mismatch, and treating it as
 *    one suppressed every anchor on the deployed preview.
 *    `PublicProductOverviewFragment` selects the top-level
 *    `buy_now_price { amount }` with no `currency` for search results,
 *    while `productBySlug` selects both, so the sold item said "EUR" and
 *    every card said nothing. `formatPrice` already reads an absent
 *    currency as EUR, so this follows the convention the codebase already
 *    has rather than inventing a second one. The formatted amount uses
 *    whichever currency IS known.
 *  - the difference is under `PRICE_ANCHOR_MIN_RATIO`. Saying "EUR 4 less"
 *    is worse than saying nothing.
 *
 * A dearer alternative is reported as dearer. Hiding it would leave a
 * visitor to discover the number themselves one click later, which is the
 * kind of small dishonesty that costs more trust than the click is worth.
 *
 * **Asking prices only.** `sold_at` is per-item transaction data and is
 * redacted out of this page entirely (`sold-price.utils.ts`), so it is not
 * merely unused here, it is unavailable.
 */
export const buildPriceAnchor = (
  soldAskingMoney: { amount?: number | null; currency?: string | null } | null,
  alternative: AlternativeProductLike
): PriceAnchor | null => {
  const reference = soldAskingMoney?.amount;
  const candidate = resolveAskingPrice(alternative);

  if (typeof reference !== "number" || reference <= 0 || !candidate) {
    return null;
  }

  const referenceCurrency = soldAskingMoney?.currency ?? null;
  const candidateCurrency = candidate.currency ?? null;

  if (
    referenceCurrency &&
    candidateCurrency &&
    referenceCurrency !== candidateCurrency
  ) {
    return null;
  }

  const difference = candidate.amount - reference;

  if (Math.abs(difference) / reference < PRICE_ANCHOR_MIN_RATIO) {
    return null;
  }

  return {
    direction: difference < 0 ? "less" : "more",
    amount: Math.abs(difference),
    currency: candidateCurrency ?? referenceCurrency,
  };
};

export const extractAlternativeCriteria = (
  product: AlternativeProductLike
): SoldAlternativeCriteria => {
  const category = deepestCategory(product?.categories);

  // `productBySlug` returns brand both as a top-level field and inside
  // `attributes`; `searchProducts` only returns the attribute. Read both
  // so the same function works on either shape.
  const brand =
    product?.brand?.slug ??
    attributeSlugs(product?.attributes, "Brand")[0] ??
    null;

  const quality = product?.quality;

  return {
    brand: brand || null,
    category: category?.slug ?? null,
    categoryRoute: category?.route ?? null,
    material: attributeSlugs(product?.attributes, "Material"),
    style: attributeSlugs(product?.attributes, "Style"),
    condition: typeof quality === "string" && quality ? quality : null,
    colour: attributeSlugs(product?.attributes, "Color"),
    // What the item was asking, never `auction.sold_at`: the band exists
    // to find items in the bracket the visitor saw. Precedence lives in
    // `asking-price.utils.ts`, the same one that prices the JSON-LD offer.
    askingPrice: resolveAskingPriceAmount(product),
  };
};

/**
 * Translated name of the leaf category, which is what the thing IS.
 *
 * Used by the "see all N comparable X" link and by the sold strip's facet
 * line, so both name the same category and neither falls back to a slug
 * when the gateway has a title. Returns null rather than a slug: a link
 * reading "See all 454 comparable tafellampen" on an English page is worse
 * than one that does not render.
 */
export const resolveCategoryTitle = (product: AlternativeProductLike) =>
  deepestCategory(product?.categories)?.title ?? null;

/** `"min,max"`, the gateway's range syntax for PRICE/WIDTH/HEIGHT/DEPTH. */
export const buildPriceBandValue = (
  askingPrice: number,
  ratio: number = PRICE_BAND_RATIO
) => {
  const round = (value: number) => Math.round(value * 100) / 100;

  return `${round(askingPrice * (1 - ratio))},${round(
    askingPrice * (1 + ratio)
  )}`;
};

/**
 * Filters for one rung, in a stable order so two calls with the same
 * criteria produce byte-identical filter sets (and identical cache keys).
 *
 * A criterion the item simply does not have is omitted rather than sent
 * empty: an empty filter value matches nothing at all, which would fail
 * every rung and send a perfectly matchable item to L5. Real inventory
 * has these gaps constantly, e.g. `de-sede-ds-7-sofa-in-cream-leather`
 * has no Style attribute at all.
 */
export const buildAlternativeFilters = (
  criteria: SoldAlternativeCriteria,
  active: CriterionKey[]
): SoldAlternativeFilter[] => {
  const filters: SoldAlternativeFilter[] = [AVAILABILITY_FILTER];

  const push = (key: string, value: string | null) => {
    if (value) {
      filters.push({ key, value });
    }
  };

  const pushMany = (key: string, values: string[]) => {
    if (values.length) {
      filters.push({ key, value: values.join(",") });
    }
  };

  // Subcategory first: it is the leading facet, and emitting it first
  // makes the logged filter set read the way the rule is stated.
  if (active.includes("category")) {
    push("CATEGORY", criteria.category);
  }

  if (active.includes("brand")) {
    push("BRAND", criteria.brand);
  }

  if (active.includes("material")) {
    pushMany("MATERIAL", criteria.material);
  }

  if (active.includes("style")) {
    pushMany("STYLE", criteria.style);
  }

  if (active.includes("condition")) {
    push(CONDITION_FILTER_KEY, criteria.condition);
  }

  if (active.includes("colour")) {
    pushMany("COLOR", criteria.colour);
  }

  return filters;
};

export const buildCategoryFallback = (
  criteria: SoldAlternativeCriteria
): SoldCategoryFallback | null =>
  criteria.category
    ? {
        slug: criteria.category,
        route: criteria.categoryRoute,
        brand: criteria.brand,
      }
    : null;

/**
 * Path for the L5 fallback link.
 *
 * The brand goes in as `?brand=<slug>`, which is the URL parameter the PLP
 * already parses into `SearchFilterKey.Brand`
 * (`search-filters-generators.utils.ts`). `localizeHref` preserves the
 * query string while translating the Dutch route segments, so the link
 * survives the locale rewrite intact.
 */
export const buildCategoryFallbackPath = (
  fallback: SoldCategoryFallback | null
) => {
  if (!fallback?.route) {
    return null;
  }

  return fallback.brand
    ? `/${fallback.route}?brand=${encodeURIComponent(fallback.brand)}`
    : `/${fallback.route}`;
};

/**
 * The facet set a "notify me when something similar arrives" alert should
 * watch: L0, the strictest rung, because the alert is about a genuinely
 * comparable piece rather than about the category refilling.
 *
 * AVAILABILITY is kept rather than stripped, matching what the PLP's own
 * save-search button stores, so the saved search deep-links back to a
 * listing page that behaves the same way.
 */
export const buildAlertFilters = (product: AlternativeProductLike) =>
  buildAlternativeFilters(
    extractAlternativeCriteria(product),
    LEVEL_CRITERIA.L0
  );

const attributeTitles = (
  attributes: Array<AttributeLike> | null | undefined,
  typename: string
) =>
  (attributes ?? [])
    .filter(attribute => attribute?.__typename === typename)
    .map(attribute => attribute?.title)
    .filter(
      (title): title is string => typeof title === "string" && !!title.trim()
    );

/**
 * Human-readable name for the saved search, so the row in
 * `/account/searches` says "de Sede Fauteuils, blue" rather than the
 * `saved_search_<timestamp>` the PLP's button stores.
 *
 * Brand, then subcategory, then colour, mirroring the leading facets. Uses
 * the gateway's translated titles when present and falls back to slugs,
 * because a title is nullable on every one of these types.
 */
export const buildAlertTitle = (product: AlternativeProductLike) => {
  const criteria = extractAlternativeCriteria(product);
  const category = deepestCategory(product?.categories);

  const brand =
    attributeTitles(product?.attributes, "Brand")[0] ?? criteria.brand;
  const categoryLabel = category?.title ?? criteria.category;
  const colours = attributeTitles(product?.attributes, "Color");
  const colourLabel = colours.length ? colours.join(", ") : criteria.colour[0];

  const head = [brand, categoryLabel].filter(Boolean).join(" ");
  const parts = [head, colourLabel].filter(Boolean);

  return parts.join(", ");
};

/**
 * A rung is only worth querying if it constrains something beyond the
 * category.
 *
 * Category plus AVAILABILITY is not a similarity search, it is "the
 * newest things in this section", and we already have a better answer for
 * that: L5 links to the category page, which says the same thing honestly
 * and without dressing two arbitrary items up as comparables.
 */
const isDiscriminating = (filters: SoldAlternativeFilter[]) =>
  filters.some(
    filter => filter.key !== "AVAILABILITY" && filter.key !== "CATEGORY"
  );

const excludeSelf = <TItem extends AlternativeProductLike>(
  items: TItem[],
  productId: string | null | undefined
) => items.filter(item => !productId || item?.id !== productId);

/**
 * Closest asking price first. Items with no price sort last rather than
 * being dropped: a missing price is a data gap, not a reason to show the
 * visitor an empty module.
 */
export const rankByPriceProximity = <TItem extends AlternativeProductLike>(
  items: TItem[],
  askingPrice: number
) =>
  [...items].sort((left, right) => {
    const leftPrice = resolveAskingPriceAmount(left);
    const rightPrice = resolveAskingPriceAmount(right);

    if (leftPrice === null && rightPrice === null) {
      return 0;
    }

    if (leftPrice === null) {
      return 1;
    }

    if (rightPrice === null) {
      return -1;
    }

    return (
      Math.abs(leftPrice - askingPrice) - Math.abs(rightPrice - askingPrice)
    );
  });

/**
 * Walk the ladder and return the first rung that yields a buyable
 * alternative, together with enough provenance to judge the match later.
 *
 * `search` is injected so the rules can be tested without a gateway. It
 * receives the filter list and a limit, and must return the page of items
 * plus the TOTAL match count (`pagination.count`), which is what the
 * price-band threshold is measured against.
 */
export const findSoldAlternatives = async <
  TItem extends AlternativeProductLike,
>({
  product,
  search,
  displayCount = ALTERNATIVES_DISPLAY_COUNT,
  fetchLimit = ALTERNATIVES_FETCH_LIMIT,
}: {
  product: AlternativeProductLike;
  search: AlternativesSearch<TItem>;
  displayCount?: number;
  fetchLimit?: number;
}): Promise<SoldAlternativesResult<TItem>> => {
  const criteria = extractAlternativeCriteria(product);
  const productId = product?.id ?? null;

  const fallback: SoldAlternativesResult<TItem> = {
    level: ALTERNATIVES_LEVEL.L5,
    alternatives: [],
    filters: [],
    priceBand: PRICE_BAND_OUTCOME.NotApplicable,
    matchCount: 0,
    categoryFallback: buildCategoryFallback(criteria),
  };

  for (const level of LEVEL_ORDER) {
    const filters = buildAlternativeFilters(criteria, LEVEL_CRITERIA[level]);

    if (!isDiscriminating(filters)) {
      continue;
    }

    const { items, count } = await search(filters, fetchLimit);
    const candidates = excludeSelf(items, productId);

    // The sold item itself is normally absent anyway (AVAILABILITY=true
    // excludes it), but the preview mode renders this treatment for a
    // live product, where it would otherwise recommend itself.
    const selfSeen = candidates.length !== items.length;
    const matchCount = Math.max(0, count - (selfSeen ? 1 : 0));

    if (!candidates.length) {
      continue;
    }

    if (matchCount <= PRICE_BAND_THRESHOLD) {
      return {
        level,
        alternatives: candidates.slice(0, displayCount),
        filters,
        priceBand: PRICE_BAND_OUTCOME.NotNeeded,
        matchCount,
        categoryFallback: null,
      };
    }

    if (criteria.askingPrice === null) {
      return {
        level,
        alternatives: candidates.slice(0, displayCount),
        filters,
        priceBand: PRICE_BAND_OUTCOME.NoAskingPrice,
        matchCount,
        categoryFallback: null,
      };
    }

    const bandedFilters: SoldAlternativeFilter[] = [
      ...filters,
      { key: "PRICE", value: buildPriceBandValue(criteria.askingPrice) },
    ];

    const banded = await search(bandedFilters, fetchLimit);
    const bandedCandidates = excludeSelf(banded.items, productId);

    if (bandedCandidates.length >= MIN_BANDED_RESULTS) {
      return {
        level,
        alternatives: bandedCandidates.slice(0, displayCount),
        filters: bandedFilters,
        priceBand: PRICE_BAND_OUTCOME.Applied,
        matchCount,
        categoryFallback: null,
      };
    }

    // The band was too tight for this level's inventory. Throw it away
    // rather than let it starve a level that had plenty of matches, and
    // use price proximity as a ranking instead of a filter.
    return {
      level,
      alternatives: rankByPriceProximity(
        candidates,
        criteria.askingPrice
      ).slice(0, displayCount),
      filters,
      priceBand: PRICE_BAND_OUTCOME.DiscardedTooNarrow,
      matchCount,
      categoryFallback: null,
    };
  }

  return fallback;
};

/**
 * One structured line per sold page render, so we can count which rung
 * actually fires on live traffic before deciding whether the ladder needs
 * more rungs or fewer.
 */
export const describeAlternativesOutcome = (
  slug: string | null | undefined,
  result: SoldAlternativesResult<unknown>
) =>
  [
    "sold-alternatives",
    `slug=${slug ?? "unknown"}`,
    `level=${result.level}`,
    `matches=${result.matchCount}`,
    `shown=${result.alternatives.length}`,
    `price-band=${result.priceBand}`,
    `filters=${
      result.filters.map(filter => `${filter.key}:${filter.value}`).join("|") ||
      "none"
    }`,
  ].join(" ");
